import type { ReactNode } from "react";
import { Link, useParams } from "react-router";
import { formStateFor } from "@/modules/booking/commands/consumerCopy";
import type { BookingState } from "@/modules/booking/surfaces";
import type { BookDayView } from "@/design/BookAppointment";
import {
  CancelBookingScreen as CancelBooking,
  RescheduleBookingScreen as RescheduleBooking,
  RescheduleSuggestionScreen as RescheduleSuggestion,
  type ActionBookingView,
  type BookingActionCommands,
} from "@/design/BookingActions";
import { DataUnavailable } from "@/design/DataUnavailable";
import { PageShell } from "@/design/ui";
import { AppLink } from "~/components/AppLink";
import { ConsumerPage } from "~/components/ConsumerChrome";
import { NotFoundScreen } from "~/screens/NotFoundScreen";
import { ScreenPending } from "~/components/ScreenPending";
import { post } from "~/lib/api";
import { useQuery } from "~/lib/useQuery";

/**
 * The three screens a WhatsApp link opens (R21): cancel, change time, answer a
 * suggestion.
 *
 * **The login round trip is why these are routes rather than links into the
 * bookings list.** Somebody tapping a link in a chat thread may have no session
 * at all, and the guard sends them to log in carrying `?next=` so they come back
 * to the exact booking. The screens themselves live in `@/design` and are shared
 * with the Next host; what is here is this app's transport and its own reading
 * of "this booking cannot be changed".
 *
 * A booking that is not theirs comes back as `not_found`, deliberately
 * indistinguishable from one that does not exist (§5, no IDOR oracle), which
 * matters more than usual here because the id travelled through a chat app.
 */
interface ActionPayload {
  booking: ActionBookingView;
  days: BookDayView[];
  canCancel: boolean;
  canReschedule: boolean;
  state: BookingState;
}

const commands = (bookingId: string): BookingActionCommands => ({
  Link: AppLink,
  cancel: async () => {
    const result = await post(`/bookings/${bookingId}/cancel`);
    return result.ok ? { success: "cancelled" } : formStateFor(result.failure);
  },
  reschedule: async (_id, date, startMinutes, endMinutes) => {
    const result = await post(`/bookings/${bookingId}/reschedule`, {
      date,
      startMinutes,
      endMinutes,
    });
    return result.ok ? { success: "moved" } : formStateFor(result.failure);
  },
  acceptSuggestion: async () => {
    const result = await post(`/bookings/${bookingId}/suggestion`, {
      accept: true,
    });
    return result.ok ? { success: "accepted" } : formStateFor(result.failure);
  },
  rejectSuggestion: async () => {
    const result = await post(`/bookings/${bookingId}/suggestion`, {
      accept: false,
    });
    return result.ok ? { success: "rejected" } : formStateFor(result.failure);
  },
});

/**
 * The frame all three share: the way back, and the three answers that are not
 * the screen itself (still loading, cannot be read, not theirs).
 */
function ActionFrame({
  children,
}: {
  children: (payload: ActionPayload, bookingId: string) => ReactNode;
}) {
  const { id } = useParams();
  const query = useQuery<ActionPayload>(id ? `/bookings/${id}` : null);

  if (query.failure === "not_found") return <NotFoundScreen />;

  return (
    <ConsumerPage>
      <PageShell>
        <div className="mx-auto w-full max-w-md">
          <Link
            to="/bookings"
            className="mb-4 inline-block text-sm font-medium text-accent"
          >
            ← Your bookings
          </Link>
          {query.loading && !query.data ? (
            <ScreenPending />
          ) : query.failure ? (
            <DataUnavailable
              configured={query.failure !== "not_configured"}
              what="this booking"
            />
          ) : query.data && id ? (
            children(query.data, id)
          ) : null}
        </div>
      </PageShell>
    </ConsumerPage>
  );
}

/**
 * What a booking that cannot be acted on says.
 *
 * The two sentences are different on purpose: inside the one-hour window there
 * is somebody to call, and after a booking has ended or been cancelled there is
 * nothing to do at all. Telling somebody to contact the clinic about an
 * appointment that already happened would be worse than saying nothing.
 */
function Closed({ title, state }: { title: string; state: BookingState }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-8 text-center shadow-sm">
      <p className="font-semibold text-ink">{title}</p>
      <p className="mx-auto mt-2 max-w-xs text-sm text-muted">
        {state === "approved"
          ? "Changes close 1 hour before the start. Please contact the business directly."
          : "It is no longer active."}
      </p>
    </div>
  );
}

export function BookingCancelScreen() {
  return (
    <ActionFrame>
      {(payload, bookingId) =>
        payload.canCancel ? (
          <CancelBooking
            booking={payload.booking}
            commands={commands(bookingId)}
          />
        ) : (
          <Closed
            title="This booking cannot be cancelled"
            state={payload.state}
          />
        )
      }
    </ActionFrame>
  );
}

export function BookingRescheduleScreen() {
  return (
    <ActionFrame>
      {(payload, bookingId) =>
        payload.canReschedule ? (
          <RescheduleBooking
            booking={payload.booking}
            days={payload.days}
            commands={commands(bookingId)}
          />
        ) : (
          <Closed title="This booking cannot be changed" state={payload.state} />
        )
      }
    </ActionFrame>
  );
}

/**
 * The clinic asked to move the appointment (H49).
 *
 * Guarded on the suggestion still being live rather than on a flag of its own,
 * because that is what the read model already answers: `suggestedWhenLabel` is
 * null the moment a suggestion is accepted, rejected, withdrawn or its own hour
 * has passed, all derived on read with nothing sweeping them.
 */
export function RescheduleSuggestionScreen() {
  return (
    <ActionFrame>
      {(payload, bookingId) =>
        payload.booking.suggestedWhenLabel ? (
          <RescheduleSuggestion
            booking={payload.booking}
            commands={commands(bookingId)}
          />
        ) : (
          <div className="rounded-xl border border-line bg-surface p-8 text-center shadow-sm">
            <p className="font-semibold text-ink">
              There is no time to answer here
            </p>
            <p className="mx-auto mt-2 max-w-xs text-sm text-muted">
              The clinic&apos;s suggestion is no longer waiting on you. Your
              bookings list has the current state.
            </p>
          </div>
        )
      }
    </ActionFrame>
  );
}
